/**
 * Site Monitoring Dashboard - TypeScript Type Definitions
 * Real-time guard clock-in status monitoring
 */

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export enum SiteStatus {
  COMPLETE = 'complete',
  PARTIAL = 'partial',
  NONE = 'none',
  NO_ASSIGNMENT = 'no_assignment',
}

export enum ClockCycleStatus {
  ON_TIME = 'on_time',
  LATE = 'late',
  MISSED = 'missed',
  PENDING = 'pending',
}

export enum PriorityLevel {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  NORMAL = 'normal',
}

export const STATUS_COLORS = {
  complete: '#107c10',
  partial: '#fde300',
  none: '#d13438',
  no_assignment: '#e1dfdd',
} as const;

export const PRIORITY_COLORS = {
  critical: '#d13438',
  high: '#f7630c',
  medium: '#fde300',
  normal: '#107c10',
} as const;

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface SiteClockStatusResponse {
  status: 'success';
  data: SiteClockStatus[];
  pagination: PaginationMeta;
  summary: StatusSummary;
  timestamp: string;
}

export interface PaginationMeta {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export interface StatusSummary {
  total_sites: number;
  green_sites: number;
  yellow_sites: number;
  red_sites: number;
  grey_sites: number;
  last_updated: string;
}

// ============================================================================
// DATA MODEL TYPES
// ============================================================================

export interface SiteClockStatus {
  site_id: number;
  site_name: string;
  site_code: string;
  client_id: number;
  client_name: string;
  zone_id: number | null;
  zone_name: string | null;
  shift_id: number;
  shift_name: string;
  shift_start_time: string;
  shift_end_time: string;
  shift_date: string;

  assigned_guards_count: number;
  clocked_in_guards_count: number;
  required_clock_cycles: number;
  current_cycle: number;
  completed_cycles: number;

  status: SiteStatus;
  compliance_percentage: number;
  priority: PriorityLevel;

  last_clock_in: string | null;
  next_expected_clock: string | null;

  guards: GuardClockDetail[];
}

export interface GuardClockDetail {
  employee_id: number;
  employee_name: string;
  employee_code: string;
  overall_compliance: number;
  cycles: CycleStatus[];
}

export interface CycleStatus {
  cycle_number: number;
  expected_time: string;
  window_start: string;
  window_end: string;
  clock_in_time: string | null;
  status: ClockCycleStatus;
  is_current: boolean;
}

// ============================================================================
// FILTER & CONFIGURATION TYPES
// ============================================================================

export interface DashboardFilters {
  zone_id: string;
  client_id: string;
  shift_id: string;
  date: string;
  show_unassigned: boolean;
}

export interface FilterOption {
  id: number | string;
  label: string;
}

export interface FilterOptions {
  zones: FilterOption[];
  clients: FilterOption[];
  shifts: FilterOption[];
}

// ============================================================================
// COMPONENT PROPS TYPES
// ============================================================================

export interface SiteMonitoringDashboardProps {
  initialZoneId?: number;
  initialClientId?: number;
  initialShiftId?: number;
}

export interface FilterBarProps {
  filters: DashboardFilters;
  onFilterChange: (filters: DashboardFilters) => void;
  zones: FilterOption[];
  clients: FilterOption[];
  shifts: FilterOption[];
  loading: boolean;
}

export interface SiteStatusGridProps {
  sites: SiteClockStatus[];
  loading: boolean;
  onSiteClick: (site: SiteClockStatus) => void;
}

export interface SiteStatusCardProps {
  site: SiteClockStatus;
  onClick: () => void;
}

export interface SiteDetailsModalProps {
  site: SiteClockStatus | null;
  open: boolean;
  onClose: () => void;
}

export interface StatusLegendProps {
  summary: StatusSummary;
  activeFilter: SiteStatus | 'all';
  onStatusFilter: (status: SiteStatus | 'all') => void;
}

export interface RefreshControlProps {
  autoRefresh: boolean;
  onToggleAutoRefresh: () => void;
  lastUpdated: string | null;
  onManualRefresh: () => void;
  isRefreshing: boolean;
  metadata?: {
    cached: boolean;
    cache_age_seconds?: number;
  };
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type StatusColor = (typeof STATUS_COLORS)[keyof typeof STATUS_COLORS];
export type PriorityColor = (typeof PRIORITY_COLORS)[keyof typeof PRIORITY_COLORS];

export interface ApiError {
  code: string;
  message: string;
  field?: string;
  details?: Record<string, any>;
}

export interface ApiErrorResponse {
  status: 'error';
  error: ApiError;
}

// ============================================================================
// HELPER TYPE GUARDS
// ============================================================================

export function isSiteClockStatusResponse(response: any): response is SiteClockStatusResponse {
  return (
    response &&
    typeof response === 'object' &&
    response.status === 'success' &&
    Array.isArray(response.data) &&
    'pagination' in response &&
    'summary' in response &&
    'timestamp' in response
  );
}

export function isApiErrorResponse(response: any): response is ApiErrorResponse {
  return (
    response &&
    typeof response === 'object' &&
    response.status === 'error' &&
    'error' in response &&
    typeof response.error === 'object' &&
    'code' in response.error &&
    'message' in response.error
  );
}

// ============================================================================
// MOCK DATA HELPERS (for development)
// ============================================================================

export function createMockCycleStatus(
  cycleNumber: number,
  status: ClockCycleStatus = ClockCycleStatus.PENDING
): CycleStatus {
  const baseTime = new Date('2026-01-16T06:00:00');
  const cycleOffset = (cycleNumber - 1) * 2 * 60 * 60 * 1000;
  const expectedTime = new Date(baseTime.getTime() + cycleOffset);

  return {
    cycle_number: cycleNumber,
    expected_time: expectedTime.toTimeString().slice(0, 8),
    window_start: new Date(expectedTime.getTime() - 15 * 60 * 1000).toTimeString().slice(0, 8),
    window_end: new Date(expectedTime.getTime() + 15 * 60 * 1000).toTimeString().slice(0, 8),
    clock_in_time:
      status !== ClockCycleStatus.MISSED && status !== ClockCycleStatus.PENDING
        ? expectedTime.toTimeString().slice(0, 8)
        : null,
    status,
    is_current: cycleNumber === 2,
  };
}

export function createMockGuardDetail(
  employeeId: number,
  name: string,
  compliance: number
): GuardClockDetail {
  return {
    employee_id: employeeId,
    employee_name: name,
    employee_code: `EMP${String(employeeId).padStart(3, '0')}`,
    overall_compliance: compliance,
    cycles: [
      createMockCycleStatus(1, ClockCycleStatus.ON_TIME),
      createMockCycleStatus(2, ClockCycleStatus.PENDING),
      createMockCycleStatus(3, ClockCycleStatus.PENDING),
      createMockCycleStatus(4, ClockCycleStatus.PENDING),
    ],
  };
}

export function createMockSiteStatus(
  siteId: number,
  status: SiteStatus = SiteStatus.COMPLETE
): SiteClockStatus {
  return {
    site_id: siteId,
    site_name: `Site ${siteId}`,
    site_code: `SITE-${String(siteId).padStart(3, '0')}`,
    client_id: 1,
    client_name: 'Demo Client',
    zone_id: 1,
    zone_name: 'Central Zone',
    shift_id: 1,
    shift_name: 'Morning Shift',
    shift_start_time: '06:00:00',
    shift_end_time: '14:00:00',
    shift_date: '2026-01-16',
    assigned_guards_count: 3,
    clocked_in_guards_count:
      status === SiteStatus.COMPLETE ? 3 : status === SiteStatus.PARTIAL ? 2 : 0,
    required_clock_cycles: 4,
    current_cycle: 2,
    completed_cycles: 1,
    status,
    compliance_percentage:
      status === SiteStatus.COMPLETE ? 100 : status === SiteStatus.PARTIAL ? 66.67 : 0,
    priority: PriorityLevel.NORMAL,
    last_clock_in: '2026-01-16T08:05:23Z',
    next_expected_clock: '2026-01-16T10:00:00Z',
    guards: [
      createMockGuardDetail(1, 'John Doe', 100),
      createMockGuardDetail(2, 'Jane Smith', 100),
      createMockGuardDetail(3, 'Mike Johnson', 100),
    ],
  };
}

export function createMockResponse(siteCount: number = 10): SiteClockStatusResponse {
  const sites: SiteClockStatus[] = [];

  for (let i = 1; i <= siteCount; i++) {
    const statusOptions = [SiteStatus.COMPLETE, SiteStatus.PARTIAL, SiteStatus.NONE];
    const randomStatus = statusOptions[i % 3];
    sites.push(createMockSiteStatus(i, randomStatus));
  }

  const greenCount = sites.filter(s => s.status === SiteStatus.COMPLETE).length;
  const yellowCount = sites.filter(s => s.status === SiteStatus.PARTIAL).length;
  const redCount = sites.filter(s => s.status === SiteStatus.NONE).length;

  return {
    status: 'success',
    data: sites,
    pagination: {
      currentPage: 1,
      totalPages: Math.ceil(siteCount / 50),
      totalItems: siteCount,
      itemsPerPage: 50,
    },
    summary: {
      total_sites: siteCount,
      green_sites: greenCount,
      yellow_sites: yellowCount,
      red_sites: redCount,
      grey_sites: 0,
      last_updated: new Date().toISOString(),
    },
    timestamp: new Date().toISOString(),
  };
}

// ============================================================================
// CONSTANTS FOR UI
// ============================================================================

export const DEFAULT_FILTERS: DashboardFilters = {
  zone_id: '',
  client_id: '',
  shift_id: '',
  date: new Date().toISOString().split('T')[0] ?? '',
  show_unassigned: false,
};

export const REFRESH_INTERVAL_MS = 60000; // 60 seconds

export const DEFAULT_ITEMS_PER_PAGE = 50;

export const MAX_ITEMS_PER_PAGE = 100;

export const STATUS_LABELS: Record<SiteStatus, string> = {
  [SiteStatus.COMPLETE]: 'Compliant',
  [SiteStatus.PARTIAL]: 'Partial',
  [SiteStatus.NONE]: 'Non-Compliant',
  [SiteStatus.NO_ASSIGNMENT]: 'Unassigned',
};

export const PRIORITY_LABELS: Record<PriorityLevel, string> = {
  [PriorityLevel.CRITICAL]: 'Critical',
  [PriorityLevel.HIGH]: 'High',
  [PriorityLevel.MEDIUM]: 'Medium',
  [PriorityLevel.NORMAL]: 'Normal',
};

export const CYCLE_STATUS_LABELS: Record<ClockCycleStatus, string> = {
  [ClockCycleStatus.ON_TIME]: 'On-time',
  [ClockCycleStatus.LATE]: 'Late',
  [ClockCycleStatus.MISSED]: 'Missed',
  [ClockCycleStatus.PENDING]: 'Pending',
};
